import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type { User } from '../lib/types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  turnstileSiteKey: string | null;
  devLoginEnabled: boolean;
  googleEnabled: boolean;
  refresh: () => Promise<void>;
  requestMagicLink: (email: string, redirectTo?: string, turnstileToken?: string) => Promise<{ developmentLink?: string }>;
  devLogin: (email: string, redirectTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  googleLogin: (redirectTo?: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [devLoginEnabled, setDevLoginEnabled] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await api.get<{ user: User | null; turnstileSiteKey: string | null; devLoginEnabled?: boolean; googleEnabled?: boolean }>('/api/auth/me');
      setUser(result.user);
      setTurnstileSiteKey(result.turnstileSiteKey);
      setDevLoginEnabled(Boolean(result.devLoginEnabled));
      setGoogleEnabled(Boolean(result.googleEnabled));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    turnstileSiteKey,
    devLoginEnabled,
    googleEnabled,
    refresh,
    async requestMagicLink(email, redirectTo = '/', turnstileToken) {
      return api.post<{ developmentLink?: string }>('/api/auth/email/start', { email, redirectTo, turnstileToken });
    },
    async devLogin(email, redirectTo = '/') {
      await api.post('/api/auth/dev-login', { email });
      window.location.href = redirectTo;
    },
    async logout() {
      await api.post('/api/auth/logout');
      setUser(null);
    },
    googleLogin(redirectTo = '/') {
      window.location.href = `/api/auth/google/start?redirectTo=${encodeURIComponent(redirectTo)}`;
    },
  }), [user, loading, turnstileSiteKey, devLoginEnabled, googleEnabled, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
